import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, useMotionValueEvent, useScroll, useAnimation, Transition } from 'framer-motion'
import tw from 'twin.macro'
import { useMediaQuery } from 'usehooks-ts'

import SearchIcon from '@/icons/bwi/search.svg'
import { useTranslation } from '@/utils/useTranslation'

const transition: Transition = {
  duration: 0.3,
  ease: 'easeOut',
}
const SCROLL_DISTANCE = 100

export default function HelpSearch(): JSX.Element {
  const { t } = useTranslation()
  const controls = useAnimation()
  const { scrollY } = useScroll()
  const [isPortaled, setIsPortaled] = useState(false)
  const isCompact = useMediaQuery('(max-width: 924px)') && isPortaled

  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (latest >= SCROLL_DISTANCE && !isPortaled) {
      controls.start({ opacity: 0, transition })
      setIsPortaled(true)
    } else if (latest < SCROLL_DISTANCE && isPortaled) {
      controls.start({ opacity: 0, transition })
      setIsPortaled(false)
    }
  })

  useEffect(() => {
    const animateIn = async () => {
      await controls.set({ opacity: 0, transition })
      controls.start({ opacity: 1, transition })
    }
    animateIn()

    return () => {
      controls.stop()
    }
  }, [isPortaled])

  const searchField = (
    <motion.div
      tw="max-w-lg relative"
      css={!isPortaled ? tw`ml-[390px] 2xl:mx-auto` : tw`ml-4 lg:ml-auto`}
      animate={controls}
    >
      <input
        name="search"
        type="text"
        placeholder={isCompact ? t('Search') : t('What are you looking for?')}
        readOnly
        tw="rounded-full mx-auto w-full placeholder:(text-sm text-indigoBlue)"
        css={[!isPortaled ? tw`py-3 px-6` : tw`py-1.5 lg:py-2 px-4 lg:px-6`, isCompact && tw`pl-10 pr-2`]}
        onKeyDown={(e) => {
          if (e.code === 'Enter' || e.code === 'Space')
            window.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'k',
                ctrlKey: true,
                metaKey: true,
              })
            )
        }}
        onClick={() => {
          window.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'k',
              ctrlKey: true,
              metaKey: true,
            })
          )
        }}
      />
      <SearchIcon
        tw="absolute text-indigoBlue top-0 bottom-0 right-4 m-auto w-4 h-4 lg:(w-5 h-5) pointer-events-none"
        css={isCompact && tw`left-4 right-auto`}
      />
    </motion.div>
  )

  return (
    <nav tw="bg-indigoBlue px-5 md:pt-3 lg:pt-0">
      <h1 tw="ml-[395px] font-normal pt-6 text-left text-white text-xl lg:text-3xl 2xl:(mx-auto text-center text-4xl)">
        {t('Welcome to the Help Center')}
      </h1>
      <div tw="relative h-[75px]">
        <div tw="absolute top-0 left-0 right-0 bottom-0 m-auto h-full z-[1]">
          {isPortaled ? createPortal(searchField, document.getElementById('search-portal')) : searchField}
        </div>
      </div>
    </nav>
  )
}
